import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Video, VideoOff, PhoneOff, AlertCircle, MicOff } from "lucide-react"
import InterfaceGuideModal from "../components/InterfaceGuideModal"
import CollaborativeEditor from "../components/CollaborativeEditor"
import aiAgentService from "../services/ai-agent-orchestrator"
import InterviewAuthGuard from "../components/InterviewAuthGuard"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { db } from "../services/firebase"
import TabSwitchWarningModal from "../components/TabSwitchWarningModal"
import { useRecording } from "../hooks/useRecording"
import FacialLandmarksMirror from "../components/FacialLandmarksMirror"
import AiInterviewTranscriptService from "../services/aiInterviewTranscriptService"

// ── AI Avatar PiP tile ────────────────────────────────────────────────────────
const AIAvatar = ({ isSpeaking, fullSize = false }) => (
  <div className="relative flex flex-col items-center justify-center h-full gap-3 bg-zinc-900 overflow-hidden">
    <div className="relative">
      <div
        className={`flex items-center justify-center transition-all duration-300 ${isSpeaking ? "ai-speaking" : ""} ${fullSize ? "w-20 h-20" : "w-10 h-10"}`}
        style={{ borderRadius: "12px", background: "rgba(255,255,255,0.08)" }}
      >
        <svg
          width={fullSize ? 40 : 22}
          height={fullSize ? 40 : 22}
          viewBox="0 0 52 52"
          fill="none"
        >
          <path
            d="M26 4L46 16V36L26 48L6 36V16L26 4Z"
            stroke="white"
            strokeWidth="2.5"
            fill="none"
          />
          <path
            d="M26 4L26 48M6 16L46 36M46 16L6 36"
            stroke="white"
            strokeWidth="1.5"
            opacity="0.4"
          />
        </svg>
      </div>
      {isSpeaking && (
        <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-400 border-2 border-zinc-900 animate-pulse rounded-full" />
      )}
    </div>
    {fullSize && (
      <span className="text-sm font-semibold text-white/60">
        AI Interviewer
      </span>
    )}
    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
      <span
        className={`w-1.5 h-1.5 rounded-full ${isSpeaking ? "bg-blue-400 animate-pulse" : "bg-zinc-500"}`}
      />
      <span className="text-white text-xs font-medium">AI Interviewer</span>
    </div>
  </div>
)

// ── User video PiP tile ───────────────────────────────────────────────────────
const UserVideoTile = ({ stream, isCameraOn, name }) => {
  const videoRef = useRef(null)
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream
      if (isCameraOn) videoRef.current.play().catch(() => {})
    }
  }, [stream, isCameraOn])
  return (
    <div className="relative h-full bg-zinc-800 flex items-center justify-center overflow-hidden">
      {isCameraOn && stream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" }}
        />
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-16 h-16 bg-zinc-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {name?.charAt(0)?.toUpperCase() || "Y"}
          </div>
          <span className="text-xs text-zinc-500 font-medium">Camera off</span>
        </div>
      )}
      <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="text-white text-xs font-medium">{name || "You"}</span>
      </div>
    </div>
  )
}

// ── Permission error screen ───────────────────────────────────────────────────
const PermissionError = ({ missingCamera, missingMic, onRetry }) => (
  <div
    className="fixed inset-0 bg-zinc-950 flex items-center justify-center z-50"
    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
  >
    <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');`}</style>
    <div
      className="bg-white p-8 max-w-md w-full mx-4 text-center"
      style={{ borderRadius: 0 }}
    >
      <div
        className="w-12 h-12 bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-5"
        style={{ borderRadius: 0 }}
      >
        <AlertCircle size={22} className="text-red-500" />
      </div>
      <h2 className="text-lg font-bold text-zinc-900 mb-1 tracking-tight">
        Permissions Required
      </h2>
      <p className="text-zinc-500 text-sm leading-relaxed mb-6">
        Allow access to continue to your AI interview.
      </p>
      <div className="space-y-2 mb-7 text-left">
        {missingCamera && (
          <div
            className="flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-3"
            style={{ borderRadius: 0 }}
          >
            <VideoOff size={16} className="text-red-500 shrink-0" />
            <div>
              <p className="text-zinc-800 text-sm font-semibold">Camera</p>
              <p className="text-zinc-500 text-xs">Required for video feed</p>
            </div>
          </div>
        )}
        {missingMic && (
          <div
            className="flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-3"
            style={{ borderRadius: 0 }}
          >
            <MicOff size={16} className="text-red-500 shrink-0" />
            <div>
              <p className="text-zinc-800 text-sm font-semibold">Microphone</p>
              <p className="text-zinc-500 text-xs">
                Required for the AI to hear you
              </p>
            </div>
          </div>
        )}
      </div>
      <button
        onClick={onRetry}
        className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-semibold py-3 text-sm transition-colors"
        style={{ borderRadius: 0 }}
      >
        Grant Permissions &amp; Continue
      </button>
      <p className="text-zinc-400 text-xs mt-4">
        Allow in your browser's address bar, then click above.
      </p>
    </div>
  </div>
)

// ── Control button ────────────────────────────────────────────────────────────
const CtrlBtn = ({ onClick, active, danger, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 border text-xs font-bold uppercase tracking-widest transition-all duration-150 ${
      danger
        ? "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-700 hover:border-zinc-700"
        : active
          ? "bg-red-50 border-red-300 text-red-600 hover:bg-red-100"
          : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-900 hover:text-zinc-900"
    }`}
    style={{ borderRadius: 0 }}
  >
    {children}
  </button>
)

// ── Interview content (post-auth) ─────────────────────────────────────────────
const InterviewContent = ({
  candidateName,
  interviewPlan,
  roomId,
  duration = 30,
}) => {
  const navigate = useNavigate()
  const [localStream, setLocalStream] = useState(null)
  const [isCameraOn, setIsCameraOn] = useState(false)
  const [permissionsGranted, setPermissionsGranted] = useState(false)
  const [missingCamera, setMissingCamera] = useState(false)
  const [missingMic, setMissingMic] = useState(false)
  const [checkingPermissions, setCheckingPermissions] = useState(true)

  const [screenPermission, setScreenPermission] = useState(null) // null | 'granted' | 'denied' | 'wrong-surface'
  const [requestingScreen, setRequestingScreen] = useState(false)
  const screenStreamRef = useRef(null)

  const [agentSpeaking, setAgentSpeaking] = useState(false)
  const [showGuideModal, setShowGuideModal] = useState(false)
  const [showEditor, setShowEditor] = useState(false)

  const [agentReady, setAgentReady] = useState(false)

  const [isEndingCall, setIsEndingCall] = useState(false)
  const [interviewTimeLeft, setInterviewTimeLeft] = useState(duration * 60)
  const warnedRef = useRef(false)
  const handleEndCallRef = useRef(null)
  const isEndingCallRef = useRef(false)

  const gazeLogRef = useRef([])

  // ── NEW: multi-question state ──────────────────────────────────────────────
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const questions = interviewPlan?.questions ?? []
  const currentQuestion = questions[currentQuestionIndex] ?? null

  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  const [showTabWarning, setShowTabWarning] = useState(false)

  const agentStarted = useRef(false)
  const timerRef = useRef(null)

  const questionIndexRef = useRef(0)
  const showEditorRef = useRef(false)

  const recording = useRecording()

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setTabSwitchCount((c) => {
          const newCount = c + 1
          aiAgentService.injectMessage(
            `[SYSTEM] The candidate just switched away from the interview tab. ` +
              `This is violation #${newCount}. ` +
              `firmly but professionally warn them that tab switching is not allowed and is being recorded. ` +
              `If violations exceed 1, tell them their interview may be disqualified.`,
          )
          return newCount
        })
        setShowTabWarning(true)
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0")
    const sec = (s % 60).toString().padStart(2, "0")
    return `${m}:${sec}`
  }

  const requestPermissions = useCallback(async () => {
    setCheckingPermissions(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })
      setLocalStream(stream)
      setIsCameraOn(true)
      setPermissionsGranted(true)
      setMissingCamera(false)
      setMissingMic(false)
    } catch {
      let camFailed = false,
        micFailed = false
      try {
        await navigator.mediaDevices.getUserMedia({ video: true })
      } catch {
        camFailed = true
      }
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch {
        micFailed = true
      }
      if (!camFailed && !micFailed) {
        camFailed = true
        micFailed = true
      }
      setMissingCamera(camFailed)
      setMissingMic(micFailed)
      setPermissionsGranted(false)
    } finally {
      setCheckingPermissions(false)
    }
  }, [])

  const requestScreenPermission = useCallback(async () => {
    setRequestingScreen(true)
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
          frameRate: { ideal: 30 },
          width: { ideal: 1920 },
        },
        audio: false,
      })
      const track = screenStream.getVideoTracks()[0]
      const surface = track.getSettings().displaySurface
      if (surface && surface !== "monitor") {
        screenStream.getTracks().forEach((t) => t.stop())
        screenStreamRef.current = null
        setScreenPermission("wrong-surface")
      } else {
        screenStreamRef.current = screenStream
        setScreenPermission("granted")
      }
    } catch {
      screenStreamRef.current = null
      setScreenPermission("denied")
    } finally {
      setRequestingScreen(false)
    }
  }, [])

  useEffect(() => {
    if (permissionsGranted && screenPermission === null) {
      requestScreenPermission()
    }
  }, [permissionsGranted, screenPermission, requestScreenPermission])

  useEffect(() => {
    requestPermissions()
  }, [requestPermissions])

  useEffect(() => {
    if (
      !permissionsGranted ||
      screenPermission !== "granted" ||
      agentStarted.current ||
      !interviewPlan
    )
      return
    agentStarted.current = true
    ;(async () => {
      await setDoc(doc(db, "reports", roomId), {})
      await setDoc(
        doc(db, "rooms", roomId),
        { feedbackStatus: "" },
        { merge: true },
      )
    })()

    aiAgentService.onSpeakingChange = (speaking) => setAgentSpeaking(speaking)
    aiAgentService.onOpenCodeEditor = () => {
      if (!showEditorRef.current) setShowGuideModal(true)
    }

    aiAgentService.onReady = () => {
      console.log("Agent ready")
      setAgentReady(true)
    }

    aiAgentService.onTranscript = (role, text, timestamps) => {
      recording.sendTranscript(role, text, timestamps)
      AiInterviewTranscriptService.addMessage(role, text).catch((err) =>
        console.error("❌ Failed to save transcript:", err),
      )
    }

    aiAgentService.onCurrentQuestion = (id) => {
      const idx = questions.findIndex((q) => q.id === id)
      if (idx !== -1) {
        questionIndexRef.current = idx
        setCurrentQuestionIndex(idx)
        const q = questions[idx]
        const shouldShow = ["coding", "system-design", "case-study"].includes(
          q?.type,
        )
        showEditorRef.current = shouldShow
        setShowEditor(shouldShow)
      }
    }

    aiAgentService.onQuestionComplete = (id, result) => {
      console.log(`Question ${id} complete: ${result}`)
    }

    aiAgentService.onInterviewEnd = () => {
      console.log("🏁 Interview ended by agent")
      handleEndCallRef.current?.()
    }

    // AFTER
    AiInterviewTranscriptService.startSession(roomId)
      .then(() =>
        aiAgentService.start(
          candidateName,
          localStream,
          interviewPlan.questions,
          roomId,
          interviewPlan.companyName,
          interviewPlan?.additionalInstructions ?? "",
        ),
      )
      .then(() => {
        aiAgentService.onAudioChunk = (pcmBuffer) => {
          recording.sendAudioChunk(pcmBuffer)
        }
        // ✅ Timer starts only after agent is confirmed running
        timerRef.current = setInterval(() => {
          setInterviewTimeLeft((prev) => {
            const next = prev - 1
            if (next === 120 && !warnedRef.current) {
              warnedRef.current = true
              aiAgentService.injectMessage(
                `[SYSTEM] There are 2 minutes remaining. Finish the current question naturally, ` +
                  `but do NOT say goodbye or conclude yet — keep the conversation going until told time is up.`,
              )
            }
            if (next === 30) {
              aiAgentService.injectMessage(
                `[SYSTEM] 30 seconds left. Wrap up any final thought, then wait — ` +
                  `do NOT say goodbye until you receive the time-is-up signal.`,
              )
            }
            if (next <= 0) {
              clearInterval(timerRef.current)
              aiAgentService.injectMessage(
                `[SYSTEM] Interview time is up. Thank ${candidateName}, let them know the ` +
                  `interview has concluded and next steps will follow, then say goodbye.`,
              )
              setTimeout(() => {
                if (!isEndingCallRef.current) handleEndCallRef.current?.()
              }, 8000)
              return 0
            }
            return next
          })
        }, 1000)
        return recording.start(localStream, roomId, screenStreamRef.current)
      })
      .catch((err) => {
        console.error("Failed to start agent or recording:", err)
        agentStarted.current = false
      })

    return () => {
      clearInterval(timerRef.current)
      recording.stop() // ← add this line
      if (agentStarted.current) {
        aiAgentService.stop()
        agentStarted.current = false
      }
      if (localStream) localStream.getTracks().forEach((t) => t.stop())
    }
  }, [permissionsGranted, screenPermission, interviewPlan])

  const toggleCamera = () => {
    if (!localStream) return
    localStream.getVideoTracks().forEach((t) => {
      t.enabled = !t.enabled
    })
    setIsCameraOn((p) => !p)
  }

  const handleEndCall = async () => {
    if (isEndingCallRef.current) return
    isEndingCallRef.current = true
    setIsEndingCall(true)
    clearInterval(timerRef.current)

    // Stop recording BEFORE stopping agent (flushes final MediaRecorder chunk)
    await recording.stop()

    if (localStream) {
      localStream.getTracks().forEach((t) => {
        t.stop()
        localStream.removeTrack(t)
      })
      setLocalStream(null)
      setIsCameraOn(false)
    }

    if (agentStarted.current) {
      agentStarted.current = false
      await aiAgentService.stop()
      await AiInterviewTranscriptService.endSession()
    }

    navigate(interviewPlan.validationRequired ? `/reports/${roomId}` : `/`)
  }

  handleEndCallRef.current = handleEndCall

  if (checkingPermissions) {
    return (
      <div
        className="fixed inset-0 bg-zinc-950 flex items-center justify-center"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
            Checking permissions
          </p>
        </div>
      </div>
    )
  }

  if (!permissionsGranted) {
    return (
      <PermissionError
        missingCamera={missingCamera}
        missingMic={missingMic}
        onRetry={requestPermissions}
      />
    )
  }

  if (screenPermission !== "granted") {
    const isWrongSurface = screenPermission === "wrong-surface"
    const isDenied = screenPermission === "denied"
    return (
      <div
        className="fixed inset-0 bg-zinc-950 flex items-center justify-center z-50"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');`}</style>
        <div
          className="bg-white p-8 max-w-md w-full mx-4 text-center"
          style={{ borderRadius: 0 }}
        >
          <div
            className={`w-12 h-12 flex items-center justify-center mx-auto mb-5 border ${
              isWrongSurface
                ? "bg-amber-50 border-amber-200"
                : isDenied
                  ? "bg-red-50 border-red-200"
                  : "bg-zinc-50 border-zinc-200"
            }`}
            style={{ borderRadius: 0 }}
          >
            <AlertCircle
              size={22}
              className={
                isWrongSurface
                  ? "text-amber-500"
                  : isDenied
                    ? "text-red-500"
                    : "text-zinc-400"
              }
            />
          </div>

          <h2 className="text-lg font-bold text-zinc-900 mb-1 tracking-tight">
            {isDenied || isWrongSurface
              ? "Screen Share Required"
              : "Share Your Entire Screen"}
          </h2>
          <p className="text-zinc-500 text-sm leading-relaxed mb-6">
            {isWrongSurface
              ? "You shared a window or tab. This interview requires your entire screen."
              : isDenied
                ? "Screen share was denied or cancelled. Please grant permission to continue."
                : 'This interview requires full screen sharing before connecting. Select "Entire Screen" when prompted.'}
          </p>

          {(isDenied || isWrongSurface) && (
            <div
              className={`flex items-start gap-3 px-4 py-3 mb-5 text-left border ${
                isWrongSurface
                  ? "bg-amber-50 border-amber-200"
                  : "bg-red-50 border-red-200"
              }`}
              style={{ borderRadius: 0 }}
            >
              <AlertCircle
                size={14}
                className={`shrink-0 mt-0.5 ${isWrongSurface ? "text-amber-500" : "text-red-500"}`}
              />
              <p
                className={`text-xs font-medium ${isWrongSurface ? "text-amber-700" : "text-red-700"}`}
              >
                {isWrongSurface
                  ? 'Please try again and select "Entire Screen" — not a window or Chrome tab.'
                  : 'Click below, allow screen sharing, then select "Entire Screen".'}
              </p>
            </div>
          )}

          <button
            onClick={requestScreenPermission}
            disabled={requestingScreen}
            className="w-full bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-400 text-white font-semibold py-3 text-sm transition-colors"
            style={{ borderRadius: 0 }}
          >
            {requestingScreen
              ? "Waiting for permission…"
              : isDenied || isWrongSurface
                ? "Try Again"
                : "Share Entire Screen & Continue"}
          </button>
          <p className="text-zinc-400 text-xs mt-4">
            When prompted, choose{" "}
            <strong className="text-zinc-600">Entire Screen</strong> — not a
            window or tab.
          </p>
        </div>
      </div>
    )
  }

  if (!agentReady) {
    return (
      <div
        className="fixed inset-0 bg-zinc-950 flex items-center justify-center"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-xs font-bold uppercase tracking-widest">
            Connecting to interviewer...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-white"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        @keyframes ai-pulse {
          0% { box-shadow: 0 0 0 0 rgba(96,165,250,0.4); }
          70% { box-shadow: 0 0 0 6px rgba(96,165,250,0); }
          100% { box-shadow: 0 0 0 0 rgba(96,165,250,0); }
        }
        .ai-speaking { animation: ai-pulse 1.4s ease-out infinite; }
      `}</style>

      {/* Top bar */}
      <div
        className="flex items-center justify-between px-5 py-2.5 bg-white border-b border-zinc-200 shrink-0"
        style={{ height: "48px" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 bg-zinc-900 flex items-center justify-center text-white text-xs font-bold"
            style={{ borderRadius: 0 }}
          >
            {candidateName.charAt(0).toUpperCase()}
          </div>
          <div className="leading-none">
            <p className="text-xs font-bold text-zinc-900">{candidateName}</p>
          </div>
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 border ml-1 transition-colors ${
              interviewTimeLeft <= 60
                ? "border-red-300 bg-red-50"
                : interviewTimeLeft <= 120
                  ? "border-amber-300 bg-amber-50"
                  : "border-zinc-200 bg-zinc-50"
            }`}
            style={{ borderRadius: 0 }}
          >
            <span
              className={`w-1.5 h-1.5 animate-pulse ${
                interviewTimeLeft <= 60
                  ? "bg-red-500"
                  : interviewTimeLeft <= 120
                    ? "bg-amber-500"
                    : "bg-red-500"
              }`}
              style={{ borderRadius: 0 }}
            />
            <span
              className={`text-xs font-bold tabular-nums ${
                interviewTimeLeft <= 60
                  ? "text-red-600"
                  : interviewTimeLeft <= 120
                    ? "text-amber-700"
                    : "text-zinc-700"
              }`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {formatElapsed(interviewTimeLeft)}
            </span>
          </div>
        </div>

        <p className="text-xs text-zinc-400 font-medium hidden md:block">
          Loving your interview experience?&nbsp;&nbsp;
          <button className="text-zinc-700 font-semibold hover:text-zinc-900 underline underline-offset-2 transition-colors">
            Book a Demo
          </button>
        </p>

        <div className="flex items-center gap-1.5">
          <CtrlBtn onClick={toggleCamera} active={!isCameraOn}>
            {isCameraOn ? <Video size={13} /> : <VideoOff size={13} />}
            <span className="hidden sm:inline">
              {isCameraOn ? "Camera" : "No Camera"}
            </span>
          </CtrlBtn>
          <CtrlBtn onClick={handleEndCall} danger>
            <PhoneOff size={13} />
            <span className="hidden sm:inline">End Interview</span>
          </CtrlBtn>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden relative">
        {showEditor ? (
          <>
            <CollaborativeEditor
              questionData={currentQuestion?.question}
              defaultLanguage={
                currentQuestion?.type === "coding" ? "cpp" : "text"
              }
            />
            <div className="absolute bottom-4 right-4 flex gap-3 z-10">
              <div
                className="overflow-hidden border border-zinc-200"
                style={{
                  width: "160px",
                  height: "112px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                }}
              >
                <AIAvatar isSpeaking={agentSpeaking} />
              </div>
              <div
                className="overflow-hidden border border-zinc-200"
                style={{
                  width: "160px",
                  height: "112px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                }}
              >
                <UserVideoTile
                  stream={localStream}
                  isCameraOn={isCameraOn}
                  name={candidateName}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center h-full bg-white">
            <div className="flex gap-4 w-full h-full px-6 py-16">
              {/* Left — candidate video, 60% */}
              <div
                className="overflow-hidden border border-zinc-200"
                style={{
                  width: "60%",
                  height: "100%",
                  borderRadius: "12px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                }}
              >
                <UserVideoTile
                  stream={localStream}
                  isCameraOn={isCameraOn}
                  name={candidateName}
                />
              </div>

              {/* Right — stacked column, 40% */}
              <div
                className="flex flex-col gap-4"
                style={{ width: "40%", height: "100%" }}
              >
                {/* Top — facial landmarks preview */}
                <div
                  className="overflow-hidden border border-zinc-200 flex-1"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                    background: "#0a0a0f",
                  }}
                >
                  <FacialLandmarksMirror
                    stream={localStream}
                    isCameraOn={isCameraOn}
                    gazeLogRef={gazeLogRef} // ← silent gaze log accumulates here
                    onGaugeFrame={recording.sendGauge}
                  />
                </div>

                {/* Bottom — AI avatar */}
                <div
                  className="overflow-hidden border border-zinc-200 flex-1"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                  }}
                >
                  <AIAvatar isSpeaking={agentSpeaking} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <InterfaceGuideModal
        isOpen={showGuideModal}
        onClose={() => {
          setShowGuideModal(false)
          setShowEditor(true)
          showEditorRef.current = true
        }}
      />

      <TabSwitchWarningModal
        isOpen={showTabWarning}
        count={tabSwitchCount}
        onDismiss={() => setShowTabWarning(false)}
      />

      {isEndingCall && (
        <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
              Ending interview
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

const AlreadyCompletedModal = () => (
  <div
    className="fixed inset-0 bg-zinc-950 flex items-center justify-center z-50"
    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
  >
    <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');`}</style>
    <div
      className="bg-white p-8 max-w-md w-full mx-4 text-center"
      style={{ borderRadius: 0 }}
    >
      <div
        className="w-12 h-12 bg-zinc-100 border border-zinc-200 flex items-center justify-center mx-auto mb-5"
        style={{ borderRadius: 0 }}
      >
        <AlertCircle size={22} className="text-zinc-500" />
      </div>
      <h2 className="text-lg font-bold text-zinc-900 mb-1 tracking-tight">
        Interview Already Completed
      </h2>
      <p className="text-zinc-500 text-sm leading-relaxed">
        You've already completed this interview. Retakes are not permitted.
      </p>
    </div>
  </div>
)

// ── Main page ─────────────────────────────────────────────────────────────────
const AiInterviewPage = () => {
  const { id } = useParams()
  const [interviewPlan, setInterviewPlan] = useState(null)
  const [verifiedName, setVerifiedName] = useState(null)
  const [planLoading, setPlanLoading] = useState(true)

  const [alreadyCompleted, setAlreadyCompleted] = useState(false)

  useEffect(() => {
    if (!id) return
    const fetchPlan = async () => {
      try {
        // Step 1: fetch room doc → get organizationId + jobListingId
        const roomSnap = await getDoc(doc(db, "rooms", id))
        if (!roomSnap.exists()) {
          console.error("Room not found:", id)
          return
        }
        const roomData = roomSnap.data()

        const { organizationId, jobListingId } = roomData.settings ?? roomData

        if (!organizationId || !jobListingId) {
          console.error("Missing organizationId or jobListingId")
          return
        }

        // Step 2: fetch jobListing doc
        const jobSnap = await getDoc(
          doc(db, "organizations", organizationId, "jobListings", jobListingId),
        )
        if (!jobSnap.exists()) {
          console.error("Job listing not found:", jobListingId)
          return
        }
        const jobData = jobSnap.data()

        const confidence = Number(roomData?.call_cut_short_confidence ?? 0)
        if (
          !jobData.validationRequired &&
          roomData?.call_cut_short_confidence !== undefined &&
          confidence <= 50
        ) {
          setAlreadyCompleted(true)
          setPlanLoading(false)
          return
        }

        // Step 3: find AI round
        const rounds = jobData?.interviewStructure?.rounds ?? []
        const aiRound = rounds.find(
          (r) => r.roundId === "round-1" && r.roundType === "Ai",
        )
        if (!aiRound) {
          console.error("AI round not found")
          return
        }

        setInterviewPlan({
          questions: aiRound.assessment ?? [],
          duration: aiRound.duration ?? 15,
          validationRequired: jobData.validationRequired ?? false,
          companyName: jobData?.organizationName,
          candidateName: roomData?.settings?.candidateInfo?.name,
          additionalInstructions: aiRound?.additionalInstructions ?? "",
        })
      } catch (err) {
        console.error("Failed to fetch interview plan:", err)
      } finally {
        setPlanLoading(false)
      }
    }
    fetchPlan()
  }, [id])

  if (planLoading) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ background: "#161b27", fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div className="flex flex-col items-center gap-8">
          {/* Spinner */}
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-2 border-white/10" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-400 animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            </div>
          </div>

          {/* Text */}
          <div className="text-center">
            <h2 className="text-white text-xl font-semibold tracking-tight mb-2">
              Setting up your interview
            </h2>
            <p className="text-white/40 text-sm">
              Please wait while we prepare everything for you
            </p>
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-3">
            {[
              { label: "Loading interview plan", delay: "0s" },
              { label: "Preparing questions", delay: "0.4s" },
              { label: "Almost ready", delay: "0.8s" },
            ].map(({ label, delay }) => (
              <div key={label} className="flex items-center gap-3">
                <div
                  className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"
                  style={{ animationDelay: delay }}
                />
                <span className="text-white/30 text-xs">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const roleName = (
    interviewPlan?.role ||
    interviewPlan?.type ||
    "AI Interview"
  )
    .replace(/-/g, " ")
    .toUpperCase()

  if (alreadyCompleted) return <AlreadyCompletedModal />

  if (!interviewPlan) return null

  if (interviewPlan.validationRequired) {
    return (
      <InterviewAuthGuard
        roleName={roleName}
        onVerified={(verifiedName) => setVerifiedName(verifiedName)}
      >
        {verifiedName && (
          <InterviewContent
            candidateName={verifiedName}
            interviewPlan={{ ...interviewPlan, candidateName: verifiedName }}
            roomId={id}
            duration={interviewPlan.duration}
          />
        )}
      </InterviewAuthGuard>
    )
  }

  return (
    <InterviewContent
      candidateName={interviewPlan?.candidateName}
      interviewPlan={interviewPlan}
      roomId={id}
      duration={interviewPlan.duration}
    />
  )
}

export default AiInterviewPage
